import { Controller, useForm, useWatch } from 'react-hook-form'
import { createAppFormContainer } from './styles.css'
import { zodResolver } from '@hookform/resolvers/zod'
import { FC, useCallback } from 'react'
import { description, flexColumn, flexRow } from '$styles/common.css'
import { BaseText } from '@genie-fintech/ui/components/hook-fields'
import Asterisk from '$components/Asterisk'
import Select from '$components/Select'
import { APP_ENVIRONMENT_OPTIONS, PERMISSIONS } from '$app/constants/common'
import ErrorField from '$components/ErrorField'
import DragAndDropFileUpload from '$components/DragAndDropFileUpload'
import { Button, Spinner } from '@genie-fintech/ui/components'
import { schema, TField, defaultValues } from './constants'
import { ChevronLeft } from 'lucide-react'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { title } from '@genie-fintech/ui/style/typography'
import { useBack, usePermissions } from '$hooks/actions'
import { useRedirectPrompt } from '$contexts/RouteContext/hooks'

const { colors } = themeVars

interface IProps {
  onSubmit: (values: TField) => void
  loading: boolean
}

const CreateAppForm: FC<IProps> = ({ onSubmit: onCreate, loading }) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty }
  } = useForm<TField>({
    resolver: zodResolver(schema),
    defaultValues
  })

  useRedirectPrompt({ shouldPrompt: isDirty })

  const { hasPrevious, onBack } = useBack({ isDirty })

  const brand = useWatch({ name: 'brand', control })

  const fileKey = useWatch({ name: 'key', control })

  const onUploadLogo = useCallback(
    (key: string) => {
      setValue('key', key, { shouldDirty: true })
    },
    [setValue]
  )

  const onChangeBrand = useCallback(
    (brand: string) => {
      setValue('brand', brand, { shouldDirty: true })
    },
    [setValue]
  )

  const onSubmit = useCallback(
    (values: TField) => {
      onCreate(values)
    },
    [onCreate]
  )

  const { checkPermissions } = usePermissions()

  const CAN_UPLOAD_LOGO = checkPermissions([PERMISSIONS.APPLICATION_EDIT_BASIC])

  return (
    <>
      <article>
        <Button
          styleVariants={{
            kind: 'neutral',
            type: 'outlined',
            size: 'small'
          }}
          style={{ background: colors.area.high }}
          onClick={onBack}
          disabled={loading}
        >
          <ChevronLeft size={16} />
          {hasPrevious ? 'Back' : 'Back to Listing'}
        </Button>
      </article>

      <form
        className={createAppFormContainer}
        onSubmit={handleSubmit(onSubmit)}
      >
        <header className={flexColumn} style={{ gap: 4 }}>
          <p className={title.two}>CREATE APPLICATION</p>
          <p className={description}>
            You can explore the data table format by downloading this template.
          </p>
        </header>

        <main className={flexColumn} style={{ gap: 16 }}>
          <BaseText
            name="name"
            control={control}
            label="Application Name"
            required
          />

          <Controller
            name="environment"
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <article className="flex flex-col gap-y-1">
                  <label className="text-sm font-medium">
                    Environment
                    <Asterisk />
                  </label>
                  <Select
                    {...field}
                    options={APP_ENVIRONMENT_OPTIONS.map(v => ({
                      label: v.value.toLocaleUpperCase(),
                      value: `${v.key}`
                    }))}
                    error={!!error?.message}
                  />
                  <ErrorField text={error?.message} />
                </article>
              )
            }}
          />

          <DragAndDropFileUpload
            brand={brand}
            logo={{ key: fileKey }}
            onUploadLogo={onUploadLogo}
            onChangeBrand={onChangeBrand}
            disabled={!CAN_UPLOAD_LOGO}
          />
        </main>

        <footer className={flexRow} style={{ justifyContent: 'end' }}>
          <Button
            styleVariants={{ size: 'small' }}
            type="submit"
            disabled={loading}
          >
            {loading && <Spinner />}
            Create
          </Button>
        </footer>
      </form>
    </>
  )
}

export default CreateAppForm
